import { getJwtExp } from '@app/utils/jwt'

import { dispose } from '@app/packages/disposable/disposable'
import type { TrackableValue } from '@app/packages/reactives/TrackableValue'

import { convertMessage } from './converters'
import type { Channel, Socket, SocketConnectOption } from './shared'
import type { ChatServerAction } from './state'
import {
  addMessage,
  applyGlobalPresenceDiff,
  applyReactionDelta,
  applyReadReceipt,
  cancelMessage,
  clearTyping,
  editMessage,
  incrementUnread,
  markReadOptimistic,
  outdateMessage,
  removeMessage,
  setConnected,
  setGlobalPresence,
  setMessageReactions,
  setReady,
  setTyping,
  setUnread,
  TYPING_TTL_MS,
} from './state'
import type { ChatServerAttachment, ChatServerMessage, ChatServerPresence, ChatServerPresenceDiff, ChatServerReaction } from './types'

export type ChatServerSocketEvent =
  | { type: 'connected' }
  | { type: 'disconnected'; reason?: string }
  /** The socket was taken down for good — it could not re-authenticate in place — and has to be rebuilt through `getToken()`, rejoining `rejoin`. */
  | { type: 'retired'; reason: string; rejoin: string[] }
  | { type: 'message'; channelId: string; message: ChatServerMessage }
  /** An operator joined or left a channel of the user: its participants on the client are stale. */
  | { type: 'participants_changed'; channelId: string }

export type ChatServerSocketListener = (event: ChatServerSocketEvent) => void

export type SocketFactory = (endpoint: string, opts: Partial<SocketConnectOption>) => Socket

export type ChatServerToken = {
  access_token: string
  /** Epoch ms of the token `exp` on this device's clock, or null when it never expires; auth is prolonged in-band (`auth:refresh`) shortly before this. */
  expires_at: number | null
}

export type ChatServerSendOptions = {
  /** Attachment message type; `text` when omitted. Kind must be homogeneous: photos → `image`, documents → `file`. */
  type?: 'image' | 'file'
  /** Presigned upload tokens the server claims on create; order defines `position`. */
  attachmentTokens?: string[]
  /** Local previews (`blob:` urls) rendered by the optimistic bubble until the echo lands. */
  attachments?: ChatServerAttachment[]
  /** Dedup key for a retry of an earlier send (≤64 chars, unique per channel+sender); the fresh clientId when omitted. */
  idempotencyKey?: string
  /** The id of a bubble already drawn for this send: the optimistic copy takes its place instead of adding another. */
  clientId?: string
}

export type ChatServerSendResult = {
  clientId: string
  /** Resolves on the server's ok reply; rejects with `ChatServerSendError` on error/timeout. Pre-handled — safe to ignore for fire-and-forget sends. */
  done: Promise<void>
}

/** `reason` of a send that never left the device: there was no live socket to push it on. */
export const OFFLINE_SEND_REASON = 'offline'

/** `reason` of a send whose push got no reply in time — see ChatServerSendTimeoutError. */
export const SEND_TIMEOUT_REASON = 'timeout'

export class ChatServerSendError extends Error {
  readonly reason: string
  /** The reply's `message` — the server's localized copy for this refusal, when it sends one. */
  readonly userMessage: string | null

  constructor(reason: string, userMessage: string | null = null) {
    super(`chat_server new_message rejected: ${reason}`)
    this.name = 'ChatServerSendError'
    this.reason = reason
    this.userMessage = userMessage
  }
}

/**
 * The push left the browser but no reply came back in time, so the send may well have
 * landed server-side. `handled` keeps it out of Sentry: the bubble is marked rejected with
 * its resend button, and the idempotency key makes the retry safe (FRONTEND-91A).
 */
export class ChatServerSendTimeoutError extends ChatServerSendError {
  readonly handled = true

  constructor() {
    super(SEND_TIMEOUT_REASON)
    this.name = 'ChatServerSendTimeoutError'
  }
}

/** The socket neither opened nor errored in time: the handshake hung, as it does on a network that went away under it. */
export class ChatServerConnectTimeoutError extends Error {
  constructor() {
    super('ChatServerSocketClient: socket connect timed out')
    this.name = 'ChatServerConnectTimeoutError'
  }
}

/**
 * A `button_click` the server refused, or one that never left the device
 * (`reason === OFFLINE_SEND_REASON`, the caller's cue to try REST instead).
 */
export class ChatServerButtonClickError extends Error {
  readonly reason: string
  /** The reply's `message` — the server's localized copy for this refusal, when it sends one. */
  readonly userMessage: string | null

  constructor(reason: string, userMessage: string | null = null) {
    super(`chat_server button_click rejected: ${reason}`)
    this.name = 'ChatServerButtonClickError'
    this.reason = reason
    this.userMessage = userMessage
  }
}

export type ChatServerSocketOptions = {
  /** Phoenix websocket endpoint, e.g. `wss://chat.kidsout.ru/socket`. */
  getWsUrl: () => string | null
  getToken: () => Promise<ChatServerToken>
  /**
   * Synchronous read of the freshest access token, used to re-authenticate
   * Phoenix's own reconnects. Optional: when absent the socket falls back to the
   * token it last connected with.
   */
  peekToken?: () => string | null
  /** The server's clock, to judge a token's `exp` by. Optional: when absent, this device's, which can run apart from it. */
  serverNow?: () => number
  /** Current UI locale, sent as the `locale` connect param (a browser WS upgrade can't carry a custom Accept-Language header). */
  getLocale?: () => string | null
  /** The current user's chat_server_uid; null while signed out / unknown. */
  getSelfUid: () => string | null
  /** Whether the user has the app in front of them; the server counts them online only then. Foreground when omitted. */
  foreground?: TrackableValue<boolean>
  /** Sink for normalized state updates (the host bridges this into its store). */
  dispatch: (action: ChatServerAction) => void
  /** Injectable for tests; defaults to the dynamically-imported phoenix `Socket`. */
  socketFactory?: SocketFactory
  log?: (...args: unknown[]) => void
}

/**
 * The chat_server websocket client: joins `channel:<id>` topics plus the
 * personal `user:<chat_server_uid>` topic, maps WS events into normalized
 * state actions, prolongs auth in-band (`auth:refresh` — no scheduled
 * reconnects), and sends optimistically.
 */
export class ChatServerSocketClient {
  private readonly options: ChatServerSocketOptions
  private socket: Socket | null = null
  private readonly channels = new Map<string, Channel>()
  private userRoom: Channel | null = null
  /** The foreground the server last heard — by join params or a `visibility` push; dedups the pushes. */
  private reportedForeground: boolean | null = null
  private foregroundWatch: Disposable | null = null
  private readonly listeners = new Set<ChatServerSocketListener>()
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private lastToken: string | null = null
  /** Expiry timers per `<channelId>:<userId>` typing marker — see `armTypingExpiry`. */
  private readonly typingExpiry = new Map<string, { channelId: string; userId: string; timer: ReturnType<typeof setTimeout> }>()

  constructor(options: ChatServerSocketOptions) {
    this.options = options
  }

  listen(listener: ChatServerSocketListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  get isConnected(): boolean {
    return !!this.socket && this.socket.isConnected()
  }

  /** Establish the websocket using the host-provided token. */
  async connect(signal: AbortSignal): Promise<void> {
    const { access_token, expires_at } = await this.options.getToken()
    signal.throwIfAborted()
    this.lastToken = access_token

    const endpoint = this.options.getWsUrl()
    if (!endpoint) throw new Error('ChatServerSocketClient: ws url is not configured')

    const factory = await (async () => {
      if (this.options.socketFactory) return this.options.socketFactory
      return await this.loadSocketFactory()
    })()
    signal.throwIfAborted()
    // Topics joined on a previous socket cannot move to a new one, so it goes down whole.
    if (this.socket) this.disconnect()

    // Pass `params` as a function, not a static object: Phoenix re-evaluates it
    // on every (re)connect — including its own transport-level auto-reconnects —
    // so a reconnect after the session token rotated re-authenticates with the
    // fresh token instead of replaying the one captured at first connect.
    const socket = factory(endpoint, { params: () => this.connectParams() })
    this.socket = socket

    // Persistent, like onError/onClose below: Phoenix reconnects the transport
    // on its own after a drop, and only a live handler sees that open — a
    // one-shot handler would leave `connected` stuck false after the first drop.
    const lifecycleRefs = [
      socket.onOpen(() => {
        this.dispatch(setConnected(true))
        this.dispatch(setReady(true))
        this.emit({ type: 'connected' })
      }),
      socket.onError(err => {
        this.log('socket error', err)
        this.dispatch(setConnected(false))
      }),
      socket.onClose(event => {
        const reason = typeof event === 'object' && event && 'reason' in event ? String((event as { reason?: unknown }).reason) : undefined
        this.dispatch(setConnected(false))
        this.emit({ type: 'disconnected', reason })
        this.retireIfTokenExpired(socket)
      }),
    ]

    try {
      await waitForOpen(socket, signal)
    } catch (err) {
      this.abandon(socket, lifecycleRefs)
      throw err
    }

    this.joinUserRoom()
    this.scheduleRefresh(expires_at)
  }

  /**
   * Stop a socket whose first open never came. Phoenix keeps reopening a failed socket on its
   * own, and a late open would set `ready` with no user topic joined and no channels loaded.
   */
  private abandon(socket: Socket, lifecycleRefs: string[]): void {
    socket.off(lifecycleRefs)
    if (this.socket === socket) this.socket = null
    socket.disconnect()
  }

  /**
   * Phoenix reopens a dropped socket with the token it reads from `params`, and chat_server
   * refuses an expired one — so a socket the server closed at `exp + leeway` would retry the
   * same token forever. Retired instead, to be rebuilt through `getToken()`, which refreshes it.
   */
  private retireIfTokenExpired(socket: Socket): void {
    if (socket !== this.socket) return
    const exp = getJwtExp(this.currentToken())
    if (exp === null || exp > (this.options.serverNow?.() ?? Date.now())) return

    this.retire('closed on an expired token')
  }

  /**
   * Take the socket down for good and report it `retired`, with the topics it had joined: the
   * host rebuilds it through `getToken()`. Keeps nothing to retry with — that policy is the
   * host's. No-op without a socket.
   */
  retire(reason: string): void {
    if (!this.socket) return
    const rejoin = [...this.channels.keys()]
    this.log('socket retired', reason)
    this.disconnect()
    this.emit({ type: 'retired', reason, rejoin })
  }

  /** Tear the socket down and leave every joined topic. */
  disconnect(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    // Flush typing markers eagerly: with the socket gone their timers die with
    // this client, and an unexpired marker would outlive it in the store.
    for (const { channelId, userId, timer } of this.typingExpiry.values()) {
      clearTimeout(timer)
      this.dispatch(clearTyping(channelId, userId))
    }
    this.typingExpiry.clear()
    for (const channel of this.channels.values()) channel.leave()
    this.channels.clear()
    if (this.foregroundWatch) dispose(this.foregroundWatch)
    this.foregroundWatch = null
    this.reportedForeground = null
    if (this.userRoom) {
      this.userRoom.leave()
      this.userRoom = null
    }
    // Detached before it closes, so its onClose knows the close was asked for.
    const socket = this.socket
    this.socket = null
    socket?.disconnect()
  }

  /** Subscribe to a channel's realtime topic (`channel:<id>`). */
  joinChannel(channelId: string): Channel {
    const socket = this.socket
    if (!socket) throw new Error('ChatServerSocketClient: socket not connected')

    const existing = this.channels.get(channelId)
    if (existing) return existing

    const channel = socket.channel(`channel:${channelId}`)
    this.channels.set(channelId, channel)
    this.wireChannel(channel, channelId)
    channel.join()
    return channel
  }

  leaveChannel(channelId: string): void {
    const channel = this.channels.get(channelId)
    if (!channel) return
    channel.leave()
    this.channels.delete(channelId)
  }

  /** Optimistic send over WS with a client id to reconcile the echo. */
  sendMessage(channelId: string, body: string, attributes: Record<string, unknown> = {}, opts?: ChatServerSendOptions): ChatServerSendResult {
    const clientId = opts?.clientId ?? `client_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const selfUid = this.options.getSelfUid()
    const type = opts?.type ?? 'text'

    const optimistic: ChatServerMessage = {
      id: clientId,
      channel_id: channelId,
      type,
      // The server keeps body NULL for caption-less attachment messages.
      body: body || null,
      attributes,
      ...(opts?.attachments?.length ? { attachments: opts.attachments } : null),
      // Self-detection keys on the sender's external_id (= our chat_server_uid),
      // so render the optimistic bubble as "mine" before the server echo lands.
      sender_id: selfUid,
      sender: selfUid ? { id: selfUid, external_id: selfUid, name: null, avatar_url: null } : null,
      edited_at: null,
      outdated_at: null,
      cancelled_at: null,
      inserted_at: new Date().toISOString(),
      pending: true,
      clientId,
    }
    this.dispatch(addMessage(optimistic))

    let resolveDone!: () => void
    let rejectDone!: (error: Error) => void
    const done = new Promise<void>((resolve, reject) => {
      resolveDone = resolve
      rejectDone = reject
    })
    // Fire-and-forget callers (plain text sends) never observe `done` — pre-handle
    // it so a server rejection doesn't escalate to an unhandled rejection.
    done.catch(() => {})

    // No socket to push on — backgrounded and not yet reconnected. Fail the send the
    // way the server would rather than throwing out of `joinChannel`: the caller has
    // already cleared its composer, so a throw is the "message vanished" bug, while a
    // rejected bubble keeps the text on screen with its resend button.
    const channel = this.socket ? (this.channels.get(channelId) ?? this.joinChannel(channelId)) : null
    if (!channel) {
      this.log('sendMessage rejected: no socket', channelId)
      this.dispatch(addMessage({ ...optimistic, pending: false, rejected: true }))
      rejectDone(new ChatServerSendError(OFFLINE_SEND_REASON))
      return { clientId, done }
    }

    channel
      .push('new_message', {
        type,
        body: body || null,
        attributes,
        // The server replays the existing message for a repeated key, so a transport-level
        // double send (rejoin re-push, double submit) cannot create a duplicate.
        idempotency_key: opts?.idempotencyKey ?? clientId,
        ...(opts?.attachmentTokens?.length ? { attachment_tokens: opts.attachmentTokens } : null),
      })
      .receive('ok', () => {
        // Server echo arrives via `message_sent`; the optimistic copy is
        // reconciled there by `clientId`. Drop the local pending marker.
        this.dispatch(removeMessage(channelId, clientId))
        resolveDone()
      })
      .receive('error', (reply: unknown) => {
        this.dispatch(addMessage({ ...optimistic, pending: false, rejected: true }))
        rejectDone(new ChatServerSendError(replyReason(reply), replyUserMessage(reply)))
      })
      .receive('timeout', () => {
        this.dispatch(addMessage({ ...optimistic, pending: false, rejected: true }))
        rejectDone(new ChatServerSendTimeoutError())
      })

    return { clientId, done }
  }

  /**
   * `mark_read` rides the channel's own topic, so a dialog nobody has joined yet — one
   * opened before the socket came up — is joined here, as `sendMessage` does. Without a
   * socket there is nothing to report on and no reason to zero the count locally: the
   * server would contradict it with its next `unread_count` anyway.
   */
  markRead(channelId: string): void {
    if (!this.socket) {
      this.log('markRead skipped: no socket', channelId)
      return
    }
    const channel = this.channels.get(channelId) ?? this.joinChannel(channelId)
    this.dispatch(markReadOptimistic(channelId))
    channel.push('mark_read', {})
  }

  /** Optimistic toggle of the caller's own reaction; the ok-reply summary reconciles drift, an error/timeout rolls back. */
  toggleReaction(channelId: string, messageId: string, emoji: string, mine: boolean): void {
    const channel = this.channels.get(channelId)
    const selfUid = this.options.getSelfUid()
    if (!channel || !selfUid) return

    this.dispatch(applyReactionDelta(channelId, messageId, emoji, selfUid, mine ? 'removed' : 'added', null))
    const rollback = () => this.dispatch(applyReactionDelta(channelId, messageId, emoji, selfUid, mine ? 'added' : 'removed', null))

    channel
      .push(mine ? 'remove_reaction' : 'add_reaction', { message_id: messageId, emoji })
      .receive('ok', (reply: { reactions?: ChatServerReaction[] }) => {
        if (reply?.reactions) this.dispatch(setMessageReactions(channelId, messageId, reply.reactions))
      })
      .receive('error', (reply: unknown) => {
        rollback()
        this.log('toggleReaction rejected', reply)
      })
      .receive('timeout', () => {
        rollback()
      })
  }

  /**
   * Press an inline-keyboard button. The server stores nothing and broadcasts nothing, so
   * this promise is the only signal there is — the keyboard itself retires when its sender
   * edits the message (`message_edited`). A drop rejects as offline rather than buffering
   * the push: phoenix would sit on it until the 10s timeout, and the caller has REST.
   */
  clickButton(channelId: string, messageId: string, callbackData: string): Promise<void> {
    const channel = this.socket?.isConnected() ? this.channels.get(channelId) : null
    if (!channel) {
      this.log('clickButton rejected: channel not joined', channelId)
      return Promise.reject(new ChatServerButtonClickError(OFFLINE_SEND_REASON))
    }

    return new Promise((resolve, reject) => {
      channel
        .push('button_click', { message_id: messageId, callback_data: callbackData })
        .receive('ok', () => resolve())
        .receive('error', (reply: unknown) => reject(new ChatServerButtonClickError(replyReason(reply), replyUserMessage(reply))))
        // A timed-out click may well have landed. Rejecting anyway re-arms the keyboard: the
        // server has no dedup, but a second press beats a keyboard dead on a press that never went.
        .receive('timeout', () => reject(new ChatServerButtonClickError(SEND_TIMEOUT_REASON)))
    })
  }

  typing(channelId: string): void {
    const channel = this.channels.get(channelId)
    channel?.push('typing', {})
  }

  /**
   * The protocol has no stop-typing event — the marker's `expiresAt` is the
   * only signal, and nothing in the store re-evaluates it once dispatched
   * (memoized selectors recompute on state changes only). This timer is what
   * actually retires the marker; each `typing` event re-arms it.
   */
  private armTypingExpiry(channelId: string, userId: string): void {
    const key = `${channelId}:${userId}`
    const existing = this.typingExpiry.get(key)
    if (existing) clearTimeout(existing.timer)
    const timer = setTimeout(() => {
      this.typingExpiry.delete(key)
      this.dispatch(clearTyping(channelId, userId))
    }, TYPING_TTL_MS)
    this.typingExpiry.set(key, { channelId, userId, timer })
  }

  private clearTypingMarker(channelId: string, userId: string): void {
    const key = `${channelId}:${userId}`
    const existing = this.typingExpiry.get(key)
    if (!existing) return
    clearTimeout(existing.timer)
    this.typingExpiry.delete(key)
    this.dispatch(clearTyping(channelId, userId))
  }

  private wireChannel(channel: Channel, channelId: string): void {
    channel.on('message_sent', (payload: unknown) => {
      const message = convertMessage(payload as Parameters<typeof convertMessage>[0])
      this.dispatch(addMessage(message))
      // The delivered message supersedes its sender's typing marker (`sender_id`
      // and the typing `user_id` are both chat_server-internal user ids).
      if (message.sender_id) this.clearTypingMarker(channelId, message.sender_id)
      this.emit({ type: 'message', channelId, message })
    })
    channel.on('message_edited', (payload: unknown) => {
      this.dispatch(editMessage(convertMessage(payload as Parameters<typeof convertMessage>[0])))
    })
    channel.on('message_cancelled', (payload: { id: string; cancelled_at: string | null }) => {
      this.dispatch(cancelMessage(channelId, payload.id, payload.cancelled_at))
    })
    channel.on('message_outdated', (payload: { id: string; outdated_at: string | null }) => {
      this.dispatch(outdateMessage(channelId, payload.id, payload.outdated_at))
    })
    channel.on('typing', (payload: { user_id: string }) => {
      this.dispatch(setTyping(channelId, payload.user_id))
      this.armTypingExpiry(channelId, payload.user_id)
    })
    channel.on('mark_read', (payload: { user_id: string; last_read_at: string }) => {
      this.dispatch(applyReadReceipt(channelId, payload.user_id, payload.last_read_at))
    })
    // Reaction deltas carry the authoritative per-emoji `count_after` — applied
    // verbatim, so our own broadcast echo after an optimistic toggle is harmless.
    channel.on('reaction_added', (payload: ReactionDeltaPayload) => {
      this.dispatch(applyReactionDelta(channelId, payload.message_id, payload.emoji, payload.actor_external_id, 'added', payload.count_after))
    })
    channel.on('reaction_removed', (payload: ReactionDeltaPayload) => {
      this.dispatch(applyReactionDelta(channelId, payload.message_id, payload.emoji, payload.actor_external_id, 'removed', payload.count_after))
    })
  }

  /**
   * Personal topic (`user:<chat_server_uid>`): `message_sent` for ALL the
   * user's channels, so the dialog list reorders and counts unread without
   * joining every channel (joined channels are skipped — their own topic
   * already delivers the full event set), plus authoritative `unread_count`
   * snapshots and the partners' global presence (`presence_state`/`presence_diff`)
   * for the dialog list. The join also tracks the user in the server's global
   * presence.
   */
  private joinUserRoom(): void {
    const socket = this.socket
    if (!socket) return
    const selfUid = this.options.getSelfUid()
    if (!selfUid) return

    // Params as a function: phoenix re-evaluates it on every (re)join, so a rejoin after a
    // reconnect reports the foreground at that moment, not the one captured at first join.
    const room = socket.channel(`user:${selfUid}`, () => {
      const foreground = this.options.foreground?.value ?? true
      this.reportedForeground = foreground
      return { visible: foreground }
    })
    this.userRoom = room
    this.watchForeground(room)
    room.on('message_sent', (payload: unknown) => {
      const message = convertMessage(payload as Parameters<typeof convertMessage>[0])
      if (this.channels.has(message.channel_id)) return
      this.dispatch(addMessage(message))
      const senderExternalId = message.sender?.external_id ?? null
      if (senderExternalId && senderExternalId !== selfUid) {
        this.dispatch(incrementUnread(message.channel_id))
      }
      this.emit({ type: 'message', channelId: message.channel_id, message })
    })
    // Authoritative per-channel unread (new message → recipients, mark_read →
    // the reader's other devices). Sent after `message_sent` on the same pipe,
    // so it reconciles the local incrementUnread guess above — which stays for
    // system messages (the server emits no unread_count for those).
    room.on('unread_count', (payload: { channel_id: string; channel_unread: number; total_unread: number }) => {
      this.dispatch(setUnread(payload.channel_id, payload.channel_unread))
    })
    // Онлайн собеседников для СПИСКА диалогов: снэпшот после join (в payload
    // только те, кто сейчас онлайн; отсутствие ключа = оффлайн) + диффы.
    room.on('presence_state', (payload: ChatServerPresence) => {
      this.dispatch(setGlobalPresence(payload))
    })
    room.on('presence_diff', (payload: ChatServerPresenceDiff) => {
      this.dispatch(applyGlobalPresenceDiff(payload))
    })
    // Оператор поддержки зашёл в канал / вышел из него: REST-состав участников
    // устарел (без него онлайн оператора в шапке не виден), хост перезагрузит канал.
    room.on('participants_changed', (payload: { channel_id: string; user_id: string; op: 'join' | 'leave' }) => {
      this.emit({ type: 'participants_changed', channelId: payload.channel_id })
    })
    room.join()
  }

  /**
   * In the background the socket stays open but nobody reads, so the server must not count the user online.
   * Pushes on change only; a room not joined carries the value in its next join params instead.
   * Old servers answer the unknown event with an error or a timeout, both ignored: the two deploy in any order.
   */
  private watchForeground(room: Channel): void {
    const foreground = this.options.foreground
    if (!foreground) return

    this.foregroundWatch = foreground.listen(value => {
      if (value === this.reportedForeground) return
      if (room.state !== 'joined') return
      this.reportedForeground = value
      room
        .push('visibility', { visible: value })
        .receive('error', (reply: unknown) => this.log('visibility rejected', reply))
        .receive('timeout', () => this.log('visibility push timed out'))
    })
  }

  /** Freshest access token for a (re)connect: the live session token, falling back to the one this socket last connected with. */
  private currentToken(): string {
    return this.options.peekToken?.() ?? this.lastToken ?? ''
  }

  /** Fresh token plus the UI locale for a (re)connect. */
  private connectParams(): Record<string, string> {
    const locale = this.options.getLocale?.()
    return { token: this.currentToken(), ...(locale ? { locale } : null) }
  }

  /** Phoenix only ever loaded here so it never lands in entry bundles. */
  private async loadSocketFactory(): Promise<SocketFactory> {
    const { Socket } = await import('phoenix')
    return (endpoint, opts) => new Socket(endpoint, opts)
  }

  private scheduleRefresh(expiresAt: number | null): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    // No `exp` ⇒ the server never rejects the token on expiry, so there is
    // nothing to pre-empt.
    if (expiresAt === null) return
    const remaining = expiresAt - Date.now()
    const delay = Math.max(0, remaining * (1 - REFRESH_LEAD_FRACTION))
    this.refreshTimer = setTimeout(() => {
      void this.refreshAuth()
    }, delay)
  }

  /**
   * Prolong the live socket's auth in-band: mint a fresh token and push it as
   * `auth:refresh` on the personal topic — the server closes an unrefreshed
   * socket at `exp + leeway`, and this replaces the old tear-down-and-reconnect
   * cycle (no presence flap, no channel rejoins). Any failure — token fetch,
   * server rejection, push timeout — retires the socket, and the host rebuilds
   * it through `getToken()`, so the worst case degrades to exactly the
   * pre-`auth:refresh` behavior.
   *
   * Public for host-driven rotations (the session refreshed outside this
   * client): an early refresh simply re-schedules the next one. Never rejects.
   */
  async refreshAuth(): Promise<void> {
    const retire = (context: string, err?: unknown) => {
      this.log(`auth refresh failed (${context})`, err)
      this.retire(`auth refresh failed: ${context}`)
    }

    const room = this.userRoom
    if (!room) {
      // No personal topic (connected without a uid) — nothing to push on.
      return retire('no user topic')
    }

    let token: ChatServerToken
    try {
      token = await this.options.getToken()
    } catch (err) {
      return retire('token fetch', err)
    }

    this.lastToken = token.access_token
    room
      .push('auth:refresh', { token: token.access_token })
      .receive('ok', () => this.scheduleRefresh(token.expires_at))
      .receive('error', (reply: unknown) => retire(`rejected: ${replyReason(reply)}`))
      .receive('timeout', () => retire('push timeout'))
  }

  private dispatch(action: ChatServerAction): void {
    this.options.dispatch(action)
  }

  private emit(event: ChatServerSocketEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (err) {
        this.log('listener threw', err)
      }
    }
  }

  private log(...args: unknown[]): void {
    this.options.log?.(...args)
  }
}

/**
 * Push `auth:refresh` once this fraction of the token's remaining lifetime is
 * left, prolonging the live socket before the server's `exp + leeway` deadline.
 * A fraction (not a fixed lead) so the cadence scales with whatever lifetime
 * the backend issues. It must stay below the host session's refresh skew
 * fraction (AuthSessionManager's `SESSION_SKEW_FRACTION` ≈ 0.2) so `getToken()`
 * lands inside the refresh window and mints a new token, rather than getting
 * the cached one back and rescheduling at ~0 delay in a busy-loop.
 * Transport-level reconnects are handled by the function-valued `params` above,
 * which re-reads the live token each time.
 */
const REFRESH_LEAD_FRACTION = 0.1

/** How long a connect may go without an open or an error before it is given up on. */
const CONNECT_TIMEOUT = 15_000

/** First open resolves, first error rejects; phoenix has no connect timeout, so this one bounds the wait. */
const waitForOpen = async (socket: Socket, signal: AbortSignal): Promise<void> => {
  using stack = new DisposableStack()
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new ChatServerConnectTimeoutError()), CONNECT_TIMEOUT)
    stack.defer(() => clearTimeout(timer))

    const onAbort = () => reject(signal.reason)
    signal.addEventListener('abort', onAbort, { once: true })
    stack.defer(() => signal.removeEventListener('abort', onAbort))

    const refs = [
      socket.onOpen(() => resolve()),
      socket.onError((err: unknown) => reject(err instanceof Error ? err : new Error('ChatServerSocketClient: socket connect failed'))),
    ]
    stack.defer(() => socket.off(refs))

    socket.connect()
  })
}

type ReactionDeltaPayload = { message_id: string; emoji: string; actor_external_id: string; count_after: number }

/** `reason` of a `new_message` error reply (`invalid_upload_token`, `attachment_rejected`, …). */
const replyReason = (reply: unknown): string => {
  const reason = typeof reply === 'object' && reply !== null ? (reply as { reason?: unknown }).reason : null
  return typeof reason === 'string' ? reason : 'error'
}

/** `message` of an error reply — server-authored copy, absent on replies that carry only a reason. */
const replyUserMessage = (reply: unknown): string | null => {
  const message = typeof reply === 'object' && reply !== null ? (reply as { message?: unknown }).message : null
  return typeof message === 'string' ? message : null
}
